import { ChevronRightOutlined } from '@mui/icons-material';
import {
  MenuItem,
  ListItemText,
  Tooltip,
  ListItemIcon,
  IconButton,
} from '@mui/material';
import { useState } from 'react';
import { tss } from 'tss-react/mui';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import { FileIcon } from '../FileIcon';

export const FILE_MENU_ITEM_FRAGMENT = graphql(`
  fragment fileMenuItemFields on File {
    _id
    filename
    folder
    owner {
      ... on Group {
        _id
        name
        machineName
      }
      ... on Service {
        _id
        name
        machineName
      }
    }
    parent {
      _id
    }
    url
    lastUploaded
    lastModified
  }
`);

export type File = DocumentType<typeof FILE_MENU_ITEM_FRAGMENT>;

interface Props {
  file: File;
  restricted: boolean;
  selected: boolean;
  disabled: boolean;
  tooltipText: string | null | undefined;
  onChange: (file?: File | null | undefined) => void;
  onChangeParent: (fileId: string) => void;
}

const useStyles = tss.create(({ theme }) => {
  const unit = parseInt(theme.spacing(1).replace('px', ''), 10);

  const extension = {
    paddingRight: unit,
    paddingLeft: unit,
    marginRight: -unit,
    marginLeft: -unit,
  };

  return {
    listItemText: {
      wordBreak: 'break-word',
    },
    listItemTextSecondary: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    fileIcon: {
      flexShrink: 0,
      marginRight: unit,
    },
    iconButton: {
      width: unit * 4,
      height: unit * 4,
      marginRight: unit,
      padding: 0,
    },
    menuItem: extension,
  };
});

export const FileMenuItem = ({
  file,
  restricted,
  selected,
  disabled,
  tooltipText,
  onChange,
  onChangeParent,
}: Props) => {
  const [mouseOver, setMouseOver] = useState(false);
  const [focused, setFocused] = useState(false);

  const { classes } = useStyles();

  const handleChangeParent = () => {
    if (file._id) {
      onChangeParent(file._id);
    }
  };

  const handleToggleFile = () => {
    const { _id, owner } = file;

    if (selected) {
      onChange();
    } else if (_id && owner && owner._id) {
      onChange(file);
    }
  };

  const handleMouseEnter = () => {
    setMouseOver(true);
  };

  const handleMouseLeave = () => {
    setMouseOver(false);
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const { _id, filename } = file;

  const content = (
    <MenuItem
      key={_id || undefined}
      disableGutters
      disabled={disabled}
      className={classes.menuItem}
      selected={selected}
      onClick={handleToggleFile}
      onDoubleClick={file.folder ? handleChangeParent : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      component={tooltipText ? 'span' : 'li'}
    >
      <FileIcon file={file} className={classes.fileIcon} />
      <ListItemText
        className={classes.listItemText}
        classes={{ secondary: classes.listItemTextSecondary }}
        secondary={
          restricted && `Only visible to members of ${file?.owner?.name || ''}`
        }
      >
        {filename}
      </ListItemText>
      {(selected || focused || mouseOver) && file.folder && !disabled && (
        <Tooltip title={`Go to "${filename || ''}"`}>
          <ListItemIcon>
            <IconButton
              className={classes.iconButton}
              onClick={handleChangeParent}
              size="large"
            >
              <ChevronRightOutlined />
            </IconButton>
          </ListItemIcon>
        </Tooltip>
      )}
    </MenuItem>
  );

  return tooltipText ? (
    <Tooltip title={tooltipText} placement="bottom-start">
      <li tabIndex={-1} style={{ outline: 'none' }}>
        {content}
      </li>
    </Tooltip>
  ) : (
    content
  );
};
